import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { COMPANY } from "@/lib/company";
import { getActiveProvince } from "@/provinces/_registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema } from "@/lib/schema";

const UPDATED = "2026-05-01";

export const Route = createFileRoute("/terms")({
  component: TermsPage,
  head: () => {
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const title = `ข้อตกลงการใช้บริการ — Thai Notary ${config.nameEn}`;
    const description = `ข้อตกลงการให้บริการของ ${COMPANY.legalName} สาขา${config.nameTh} — ขอบเขตบริการ Notary, แปล, รับรองกงสุล, การชำระเงิน, การคืนเงิน, ข้อจำกัดความรับผิด`;
    const head = pageHeadForProvince({ title, description, path: "/terms" });
    return {
      ...head,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "ข้อตกลงการใช้บริการ", url: `${site}/terms` },
            ]),
          ),
        },
      ],
    };
  },
});

function TermsPage() {
  const { config } = getActiveProvince();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-14">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Terms of Service</p>
        <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">ข้อตกลงการใช้บริการ</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          อัปเดตล่าสุด: {new Date(UPDATED).toLocaleDateString("th-TH", { year: "numeric", month: "long", day: "numeric" })}
        </p>

        <div className="prose prose-sm mt-8 max-w-none space-y-6 text-foreground">
          <section>
            <h2 className="text-xl font-bold">1. คู่สัญญา</h2>
            <p className="text-sm text-muted-foreground">
              ข้อตกลงนี้ทำขึ้นระหว่าง {COMPANY.legalName} (เรียกว่า "ผู้ให้บริการ") กับผู้ใช้บริการ (เรียกว่า "ลูกค้า")
              ในจังหวัด{config.nameTh} เมื่อลูกค้าได้ติดต่อ ส่งเอกสาร หรือชำระมัดจำให้ถือว่ายอมรับข้อตกลงนี้แล้ว
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">2. ขอบเขตบริการ</h2>
            <ul className="ml-5 list-disc text-sm text-muted-foreground">
              <li>Notary Public โดยทนายความผู้ทำคำรับรองฯ ที่ขึ้นทะเบียนสภาทนายความฯ</li>
              <li>แปลรับรองโดยนักแปลขึ้นทะเบียนกระทรวงยุติธรรม / NAATI</li>
              <li>รับรองกงสุล MFA, สถานทูต, Apostille</li>
              <li>ที่ปรึกษากฎหมายและบริการวีซ่า (LTR / DTV / Marriage / Retirement)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold">3. ราคาและการชำระเงิน</h2>
            <p className="text-sm text-muted-foreground">
              ราคาที่แสดงในเว็บไซต์เป็นราคาเริ่มต้นต่อหน้า/ต่อชุด ราคาจริงขึ้นกับความซับซ้อน จำนวนหน้า และระยะเวลา
              ลูกค้าจะได้รับใบเสนอราคาก่อนเริ่มงาน ชำระมัดจำ 50% สำหรับงานเร่งด่วน
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">4. ระยะเวลาดำเนินงาน</h2>
            <p className="text-sm text-muted-foreground">
              ระยะเวลาที่ระบุเป็นการประมาณการณ์เท่านั้น ขึ้นกับระยะเวลาของหน่วยงานราชการ (MFA, สถานทูต, ตำรวจ)
              ผู้ให้บริการจะแจ้งกำหนดส่งล่วงหน้าและไม่รับผิดในความล่าช้าที่เกิดจากเหตุสุดวิสัย
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">5. ความถูกต้องของข้อมูล</h2>
            <p className="text-sm text-muted-foreground">
              ลูกค้ารับผิดชอบในการให้ข้อมูลและเอกสารที่ถูกต้อง ครบถ้วน เป็นจริง
              หากเอกสารปลอม/แปลงเอกสาร ผู้ให้บริการขอสงวนสิทธิ์ปฏิเสธงานและแจ้งหน่วยงานที่เกี่ยวข้องตามกฎหมาย
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">6. นโยบายการคืนเงิน</h2>
            <ul className="ml-5 list-disc text-sm text-muted-foreground">
              <li>ยกเลิกก่อนเริ่มงาน — คืน 100%</li>
              <li>ยกเลิกหลังเริ่มงานแต่ยังไม่ส่งหน่วยราชการ — คืนตามสัดส่วน หักค่าดำเนินการ</li>
              <li>งานส่งหน่วยราชการแล้ว — ไม่สามารถคืนเงินค่าธรรมเนียมราชการได้</li>
              <li>กรณีงานผิดพลาดจากผู้ให้บริการ — แก้ไขให้ฟรีหรือคืนเงินเต็มจำนวน</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold">7. ข้อจำกัดความรับผิด</h2>
            <p className="text-sm text-muted-foreground">
              ความรับผิดของผู้ให้บริการจำกัดไม่เกินค่าบริการที่ลูกค้าได้ชำระสำหรับงานนั้นๆ
              ผู้ให้บริการไม่รับผิดในความเสียหายทางอ้อม การสูญเสียโอกาสทางธุรกิจ หรือผลกระทบจากการปฏิเสธของหน่วยงานปลายทาง
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">8. ทรัพย์สินทางปัญญา</h2>
            <p className="text-sm text-muted-foreground">
              เนื้อหา โลโก้ และเอกสารบนเว็บไซต์เป็นของ {COMPANY.legalName} ห้ามคัดลอก ดัดแปลง หรือใช้เพื่อการพาณิชย์โดยไม่ได้รับอนุญาตเป็นลายลักษณ์อักษร
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">9. การรักษาความลับทางวิชาชีพ</h2>
            <p className="text-sm text-muted-foreground">
              ทนายความและพนักงานปฏิบัติตามจรรยาบรรณวิชาชีพและข้อบังคับสภาทนายความฯ
              ข้อมูลของลูกค้าถือเป็นความลับ ยกเว้นตามคำสั่งศาลหรือกฎหมาย AML
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">10. กฎหมายที่ใช้บังคับและเขตอำนาจศาล</h2>
            <p className="text-sm text-muted-foreground">
              ข้อตกลงนี้อยู่ภายใต้กฎหมายแห่งราชอาณาจักรไทย ข้อพิพาทที่ไม่สามารถไกล่เกลี่ยได้ให้อยู่ภายใต้เขตอำนาจของศาลจังหวัด{config.nameTh}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">11. ติดต่อ</h2>
            <p className="text-sm text-muted-foreground">
              อีเมล {COMPANY.email} · โทร {COMPANY.phone} · LINE {COMPANY.lineId}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">12. การยืนยันตัวตน (KYC) และการป้องกันการฟอกเงิน (AML)</h2>
            <p className="text-sm text-muted-foreground">
              ตามกฎหมายป้องกันและปราบปรามการฟอกเงิน พ.ศ. 2542 และข้อบังคับสภาทนายความฯ ลูกค้าทุกราย
              ต้องผ่านกระบวนการ KYC (Know Your Customer) โดยแสดง (1) บัตรประจำตัวประชาชน/หนังสือเดินทาง
              (2) เอกสารยืนยันที่อยู่ปัจจุบัน (3) แหล่งที่มาของรายได้ (สำหรับธุรกรรมตั้งแต่ 2 ล้านบาทขึ้นไป)
              บริษัทขอสงวนสิทธิ์ปฏิเสธการให้บริการหากลูกค้าไม่สามารถผ่าน KYC ได้
              หรือพบความเสี่ยงเกี่ยวข้องกับการฟอกเงิน การให้สินบนต่างประเทศ (FCPA/UKBA)
              หรือกิจกรรมต้องสงสัยตามรายชื่อ UN Sanctions, OFAC, EU Restrictive Measures
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">13. ระยะเวลาการเก็บรักษาเอกสาร</h2>
            <p className="text-sm text-muted-foreground">
              เอกสารงาน Notary และทะเบียนงานทนายความเก็บไว้ 10 ปี ตามข้อบังคับสภาทนายความฯ ว่าด้วยทะเบียนทนาย พ.ศ. 2540
              เอกสารการเงิน/ใบกำกับภาษีเก็บไว้ 5 ปี ตามประมวลรัษฎากร เอกสาร KYC/AML เก็บไว้ 5 ปี
              นับจากวันสิ้นสุดความสัมพันธ์ตามพระราชบัญญัติ ปปง. ลูกค้าสามารถขอสำเนาเอกสารของตนเองได้ตลอดอายุการจัดเก็บ
              โดยเสียค่าธรรมเนียมการคัดลอกเริ่มต้น 200 บาทต่อเอกสารและจัดส่งทางไปรษณีย์ EMS ลงทะเบียน
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">14. การใช้บริการที่ปรึกษาภายนอก</h2>
            <p className="text-sm text-muted-foreground">
              ในบางกรณี เช่น งานวีซ่าประเทศปลายทาง, งานแปลภาษาที่ไม่ใช่ภาษาหลัก (เช่น Hebrew, Arabic, Swedish, Finnish)
              หรืองาน Apostille ในประเทศที่ไม่มีสาขาตรง บริษัทอาจมอบหมายให้ตัวแทนหรือคู่ค้าที่ได้รับการรับรอง
              (เช่น NAATI ในออสเตรเลีย, ATA ในสหรัฐฯ, ITI ในอังกฤษ) เป็นผู้ดำเนินการแทน
              โดยลูกค้าตกลงว่าบริษัทยังคงเป็นคู่สัญญาหลักและรับผิดชอบในผลงานสุดท้าย
              ตัวแทนทุกรายผ่านการคัดเลือกจากคุณสมบัติทางวิชาชีพและอยู่ภายใต้ NDA
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">15. การยกเลิกสัญญาโดยผู้ให้บริการ</h2>
            <p className="text-sm text-muted-foreground">
              บริษัทขอสงวนสิทธิ์ยกเลิกการให้บริการในกรณีต่อไปนี้
              (ก) พบว่าเอกสารหรือข้อมูลที่ลูกค้าให้เป็นเท็จ หรือมีเจตนาฉ้อโกง
              (ข) วัตถุประสงค์ของงานขัดต่อกฎหมาย ศีลธรรมอันดี หรือจรรยาบรรณวิชาชีพทนายความ
              (ค) ลูกค้าใช้พฤติกรรมก้าวร้าว คุกคาม หรือล่วงละเมิดทีมงาน
              (ง) ลูกค้าไม่ชำระค่าบริการตามกำหนดเกิน 30 วัน
              ในกรณีดังกล่าว บริษัทจะคืนเงินส่วนที่ยังไม่ได้ใช้บริการตามสมควร และส่งคืนเอกสารต้นฉบับให้ลูกค้าภายใน 7 วันทำการ
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">16. เหตุสุดวิสัย (Force Majeure)</h2>
            <p className="text-sm text-muted-foreground">
              บริษัทไม่ต้องรับผิดในความล่าช้าหรือไม่สามารถปฏิบัติตามสัญญาอันเนื่องมาจากเหตุสุดวิสัย เช่น
              ภัยพิบัติทางธรรมชาติ การประท้วงในกรุงเทพ การปิดทำการของกระทรวงการต่างประเทศหรือสถานทูต
              ภาวะฉุกเฉินด้านสาธารณสุข (เช่น COVID-19) คำสั่งของรัฐบาล, สงคราม, การก่อการร้าย
              หรือเหตุการณ์ Cyber Attack ที่ไม่อาจหลีกเลี่ยงได้ บริษัทจะแจ้งลูกค้าทันทีและร่วมกันหาแนวทางแก้ไข
              เช่น เลื่อนกำหนดส่งงาน หรือคืนเงินตามสัดส่วน
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">17. การโฆษณาและการอ้างอิงผลงาน</h2>
            <p className="text-sm text-muted-foreground">
              บริษัทไม่ใช้ข้อมูลลูกค้าเพื่อการโฆษณาหรือกรณีศึกษาโดยไม่ได้รับความยินยอมเป็นลายลักษณ์อักษร
              รีวิวและประจักษ์พยานในเว็บไซต์เป็นรีวิวจริงจากลูกค้าที่ยินยอมเผยแพร่
              และเก็บหลักฐานการยินยอมไว้ทุกชิ้น บริษัทห้ามทีมงานเปิดเผยรายชื่อหรือรายละเอียดงานต่อบุคคลภายนอกในทุกช่องทาง
              รวมถึง Social Media ส่วนตัวของพนักงาน
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">18. การประกันวิชาชีพ (Professional Indemnity Insurance)</h2>
            <p className="text-sm text-muted-foreground">
              ทนายผู้ให้บริการ Notary ในนามบริษัทมีการประกันความรับผิดทางวิชาชีพตามมาตรฐานสภาทนายความฯ
              วงเงินคุ้มครองครอบคลุมความผิดพลาดหรือประมาทเลินเล่อในงานวิชาชีพสูงสุด 5 ล้านบาทต่อเหตุการณ์
              ลูกค้าสามารถขอใบรับรองการประกันได้ผ่าน DPO กรณีลูกค้าต้องการวงเงินคุ้มครองเพิ่มเติม
              (เช่นงานธุรกรรมมูลค่าสูง) สามารถซื้อกรมธรรม์เสริมแยกต่างหากได้
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">19. การไม่ใช่ที่ปรึกษากฎหมายต่างประเทศ</h2>
            <p className="text-sm text-muted-foreground">
              บริษัทเป็นสำนักงานทนายความที่ขึ้นทะเบียนกับสภาทนายความแห่งประเทศไทย และให้คำปรึกษาเฉพาะกฎหมายไทยและขั้นตอน
              Notary/Legalization สำหรับใช้ในต่างประเทศ บริษัทไม่ได้เป็น Attorney-at-Law ในประเทศปลายทาง
              และคำแนะนำที่ให้เกี่ยวกับขั้นตอนการยื่นวีซ่าหรือกฎหมายต่างประเทศเป็นเพียงข้อมูลทั่วไป
              ลูกค้าควรปรึกษา Attorney/Solicitor ที่มีใบอนุญาตในประเทศปลายทางสำหรับข้อกฎหมายเฉพาะ
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">20. การระงับข้อพิพาท</h2>
            <p className="text-sm text-muted-foreground">
              คู่สัญญาตกลงว่าหากเกิดข้อพิพาทจะพยายามเจรจาไกล่เกลี่ยกันด้วยความสุจริตก่อนเป็นอันดับแรก
              หากไกล่เกลี่ยไม่สำเร็จภายใน 30 วัน คู่สัญญาตกลงนำข้อพิพาทเข้าสู่กระบวนการอนุญาโตตุลาการของ
              สถาบันอนุญาโตตุลาการ (THAC) หรือยื่นฟ้องต่อศาลจังหวัด{config.nameTh} ตามแต่กรณี
              ค่าธรรมเนียมและค่าทนายในกระบวนการดังกล่าวเป็นภาระของฝ่ายที่แพ้คดี
              ลูกค้ามีสิทธิร้องเรียนต่อสภาทนายความฯ ในกรณีจรรยาบรรณวิชาชีพได้ตลอดเวลาโดยไม่เสียค่าธรรมเนียม
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold">21. การโอนสิทธิและหน้าที่</h2>
            <p className="text-sm text-muted-foreground">
              ลูกค้าไม่สามารถโอนสิทธิหรือหน้าที่ตามสัญญานี้ให้บุคคลอื่นได้โดยไม่ได้รับความยินยอมเป็นลายลักษณ์อักษรจากบริษัท
              บริษัทอาจโอนสิทธิและหน้าที่ให้บริษัทในเครือหรือผู้สืบสิทธิตามกฎหมายได้
              โดยจะแจ้งลูกค้าล่วงหน้าไม่น้อยกว่า 30 วัน หากลูกค้าไม่ประสงค์ดำเนินการต่อกับผู้สืบสิทธิ
              สามารถยกเลิกสัญญาและขอคืนเงินส่วนที่ยังไม่ได้ใช้บริการได้เต็มจำนวน
            </p>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}